import { CheckedState } from "@radix-ui/react-checkbox"
import { InvalidateQueryFilters, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"

import useUser from "@hooks/useUser"

import { Checkbox } from "@atoms/index"

import GenericForm from "@molecules/Form/GenericForm/GenericForm"
import { Property } from "@molecules/index"

import { PropertyStack } from "@organisms/index"

import { Settings } from "@templates/index"

import {
    JobsAssignmentNotificationTopics,
    JobsNotificationTopics,
    NotificationMethods,
} from "@pages/Settings/SettingsNotifications/SettingsNotification.types"
import {
    notificationPreferenceQueryKey,
    useNotificationPreferencesQuery,
} from "@pages/Settings/SettingsNotifications/SettingsNotifications.store"
import EmailNotificationTooltip from "@pages/Settings/SettingsNotifications/components/EmailNotificationTooltip/EmailNotificationTooltip"
import NotificationHeader from "@pages/Settings/SettingsNotifications/components/NotificationHeader/NotificationHeader"
import { useNotificationMethodsQuery } from "@pages/Settings/SettingsNotifications/components/NotificationMethodsSection/NotificationMethodsSection.store"
import SMSNotificationTooltip from "@pages/Settings/SettingsNotifications/components/SMSNotificationTooltip/SMSNotificationTooltip"

import { NOTIFICATION_ENDPOINTS } from "@endpoints/notification"

export default function JobsAssignmentNotificationsForm() {
    const { user } = useUser()

    const [shouldEnableAllEmailNotifications, setShouldEnableAllEmailNotifications] = useState<boolean>(false)
    const [shouldDisableAllEmailNotifications, setShouldDisableAllEmailNotifications] = useState<boolean>(false)
    const [shouldEnableAllSmsNotifications, setShouldEnableAllSmsNotifications] = useState<boolean>(false)
    const [shouldDisableAllSmsNotifications, setShouldDisableAllSmsNotifications] = useState<boolean>(false)
    const [shouldSetDefaultFormData, setShouldSetDefaultFormData] = useState<boolean>(false)

    const { topicPreference: jobsNotificationPreference, isLoading: isLoadingData } =
        useNotificationPreferencesQuery<JobsNotificationTopics>("jobs")

    const { data: defaultMethodsPreference } = useNotificationMethodsQuery()

    const queryClient = useQueryClient()

    useEffect(() => {
        if (!isLoadingData && !!jobsNotificationPreference) {
            setShouldSetDefaultFormData(true)
        }
    }, [isLoadingData])

    const handleToggleAllNotifications = (method: NotificationMethods, shouldEnableAllNotifications: CheckedState) => {
        if (method === "email") {
            setShouldDisableAllEmailNotifications(!shouldEnableAllNotifications)
            setShouldEnableAllEmailNotifications(!!shouldEnableAllNotifications)
        } else {
            setShouldDisableAllSmsNotifications(!shouldEnableAllNotifications)
            setShouldEnableAllSmsNotifications(!!shouldEnableAllNotifications)
        }
    }

    const phoneNotificationsDisabled = !user?.phone || !user?.phone_verified || !defaultMethodsPreference?.contact_sms
    const emailNotificationsDisabled =
        !user?.email || !user?.email_verified || !defaultMethodsPreference?.contact_email

    const modifyFieldset = (
        fieldset: JobsAssignmentNotificationTopics,
        method: NotificationMethods,
        value: boolean,
    ) => {
        return Object.keys(fieldset).reduce((acc, key) => {
            const notificationKey = key as keyof JobsAssignmentNotificationTopics
            if (notificationKey.endsWith(`_${method}`)) {
                acc[notificationKey] = value
            }
            return acc
        }, fieldset)
    }

    const formValues = useMemo(() => {
        if (shouldEnableAllEmailNotifications) {
            return modifyFieldset(jobsNotificationPreference.assignment, "email", true)
        } else if (shouldEnableAllSmsNotifications) {
            return modifyFieldset(jobsNotificationPreference.assignment, "sms", true)
        } else if (shouldDisableAllEmailNotifications) {
            return modifyFieldset(jobsNotificationPreference.assignment, "email", false)
        } else if (shouldDisableAllSmsNotifications) {
            return modifyFieldset(jobsNotificationPreference.assignment, "sms", false)
        } else if (shouldSetDefaultFormData) {
            return jobsNotificationPreference.assignment
        } else {
            return undefined
        }
    }, [
        shouldSetDefaultFormData,
        shouldEnableAllEmailNotifications,
        shouldDisableAllEmailNotifications,
        shouldEnableAllSmsNotifications,
        shouldDisableAllSmsNotifications,
    ])

    const mutationTriggerPayload = useMemo(() => {
        if (shouldEnableAllEmailNotifications) {
            return modifyFieldset(jobsNotificationPreference.assignment, "email", true)
        } else if (shouldEnableAllSmsNotifications) {
            return modifyFieldset(jobsNotificationPreference.assignment, "sms", true)
        } else if (shouldDisableAllEmailNotifications) {
            return modifyFieldset(jobsNotificationPreference.assignment, "email", false)
        } else if (shouldDisableAllSmsNotifications) {
            return modifyFieldset(jobsNotificationPreference.assignment, "sms", false)
        } else {
            return undefined
        }
    }, [
        shouldEnableAllEmailNotifications,
        shouldDisableAllEmailNotifications,
        shouldEnableAllSmsNotifications,
        shouldDisableAllSmsNotifications,
    ])

    const resetNotificationStates = () => {
        setShouldEnableAllEmailNotifications(false)
        setShouldDisableAllEmailNotifications(false)
        setShouldEnableAllSmsNotifications(false)
        setShouldDisableAllSmsNotifications(false)
    }

    const onMutationSuccess = () => {
        void queryClient.invalidateQueries([notificationPreferenceQueryKey] as InvalidateQueryFilters)
    }

    const onAfterFormValuesSet = () => {
        resetNotificationStates()
        setShouldSetDefaultFormData(false)
    }

    return (
        <GenericForm<
            JobsAssignmentNotificationTopics,
            JobsAssignmentNotificationTopics,
            Error,
            JobsAssignmentNotificationTopics
        >
            formConfig={{
                defaultValues: undefined,
            }}
            mutationConfig={{
                genericErrorMessage: "Could not update notification preference",
                genericSuccessMessage: "Notification preference updated",
                method: "PATCH",
                endpoint: NOTIFICATION_ENDPOINTS.UPDATE_NOTIFICATION_PREFERENCES,
            }}
            onMutationSuccess={onMutationSuccess}
            mutationTriggerPayload={mutationTriggerPayload}
            formValues={formValues}
            onAfterFormValuesSet={onAfterFormValuesSet}
            isAutoSave={true}
        >
            {({ form }) => {
                const formValues = form.getValues()

                const emailValues = Object.entries(formValues)
                    .filter(([key]) => key.endsWith("_email"))
                    .map(([, value]) => value)

                const smsValues = Object.entries(formValues)
                    .filter(([key]) => key.endsWith("_sms"))
                    .map(([, value]) => value)

                const isAllNotificationsEmailEnabled = emailValues.every(Boolean)
                const isAllNotificationsSmsEnabled = smsValues.every(Boolean)
                const isSomeNotificationsEmailEnabled = emailValues.some(Boolean)
                const isSomeNotificationsSmsEnabled = smsValues.some(Boolean)

                return (
                    <Settings.NotificationConfigPage.Wrapper>
                        <NotificationHeader.Root>
                            <NotificationHeader.EmailCheckbox
                                isIndeterminate={isSomeNotificationsEmailEnabled && !isAllNotificationsEmailEnabled}
                                isChecked={isAllNotificationsEmailEnabled}
                                onChecked={(checked) => handleToggleAllNotifications("email", checked)}
                            />
                            <NotificationHeader.SMSCheckbox
                                isIndeterminate={isSomeNotificationsSmsEnabled && !isAllNotificationsSmsEnabled}
                                isChecked={isAllNotificationsSmsEnabled}
                                onChecked={(checked) => handleToggleAllNotifications("sms", checked)}
                            />
                        </NotificationHeader.Root>
                        <PropertyStack>
                            <Property.Root>
                                <Property.Content>
                                    <Property.Label>Job assigned</Property.Label>
                                    <Property.Subtitle>Notify me when I am assigned a job.</Property.Subtitle>
                                </Property.Content>
                                <Property.Value>
                                    <Settings.NotificationConfigPage.Checkboxes>
                                        <EmailNotificationTooltip>
                                            <Checkbox<JobsAssignmentNotificationTopics>
                                                size="md"
                                                colorScheme="gray"
                                                isDisabled={isLoadingData || emailNotificationsDisabled}
                                                name="enable_job_notification_job_assigned_email"
                                                isControlled={true}
                                            />
                                        </EmailNotificationTooltip>
                                        <SMSNotificationTooltip>
                                            <Checkbox<JobsAssignmentNotificationTopics>
                                                size="md"
                                                colorScheme="gray"
                                                isDisabled={isLoadingData || phoneNotificationsDisabled}
                                                name="enable_job_notification_job_assigned_sms"
                                                isControlled={true}
                                            />
                                        </SMSNotificationTooltip>
                                    </Settings.NotificationConfigPage.Checkboxes>
                                </Property.Value>
                            </Property.Root>
                            <Property.Root>
                                <Property.Content>
                                    <Property.Label>Job unassigned</Property.Label>
                                    <Property.Subtitle>Notify me when I am unassigned from a job.</Property.Subtitle>
                                </Property.Content>
                                <Property.Value>
                                    <Settings.NotificationConfigPage.Checkboxes>
                                        <EmailNotificationTooltip>
                                            <Checkbox<JobsAssignmentNotificationTopics>
                                                size="md"
                                                colorScheme="gray"
                                                isDisabled={isLoadingData || emailNotificationsDisabled}
                                                name="enable_job_notification_job_unassigned_email"
                                                isControlled={true}
                                            />
                                        </EmailNotificationTooltip>
                                        <SMSNotificationTooltip>
                                            <Checkbox<JobsAssignmentNotificationTopics>
                                                size="md"
                                                colorScheme="gray"
                                                isDisabled={isLoadingData || phoneNotificationsDisabled}
                                                name="enable_job_notification_job_unassigned_sms"
                                                isControlled={true}
                                            />
                                        </SMSNotificationTooltip>
                                    </Settings.NotificationConfigPage.Checkboxes>
                                </Property.Value>
                            </Property.Root>
                            <Property.Root>
                                <Property.Content>
                                    <Property.Label>Job updated</Property.Label>
                                    <Property.Subtitle>
                                        Notify me when someone updates a job I was assigned.
                                    </Property.Subtitle>
                                </Property.Content>
                                <Property.Value>
                                    <Settings.NotificationConfigPage.Checkboxes>
                                        <EmailNotificationTooltip>
                                            <Checkbox<JobsAssignmentNotificationTopics>
                                                size="md"
                                                colorScheme="gray"
                                                isDisabled={isLoadingData || emailNotificationsDisabled}
                                                name="enable_job_notification_job_updated_email"
                                                isControlled={true}
                                            />
                                        </EmailNotificationTooltip>
                                        <SMSNotificationTooltip>
                                            <Checkbox<JobsAssignmentNotificationTopics>
                                                size="md"
                                                colorScheme="gray"
                                                isDisabled={isLoadingData || phoneNotificationsDisabled}
                                                name="enable_job_notification_job_updated_sms"
                                                isControlled={true}
                                            />
                                        </SMSNotificationTooltip>
                                    </Settings.NotificationConfigPage.Checkboxes>
                                </Property.Value>
                            </Property.Root>
                            <Property.Root>
                                <Property.Content>
                                    <Property.Label>Job rescheduled</Property.Label>
                                    <Property.Subtitle>
                                        Notify me when someone reschedules a job I was assigned.
                                    </Property.Subtitle>
                                </Property.Content>
                                <Property.Value>
                                    <Settings.NotificationConfigPage.Checkboxes>
                                        <EmailNotificationTooltip>
                                            <Checkbox<JobsAssignmentNotificationTopics>
                                                size="md"
                                                colorScheme="gray"
                                                isDisabled={isLoadingData || emailNotificationsDisabled}
                                                name="enable_job_notification_job_rescheduled_email"
                                                isControlled={true}
                                            />
                                        </EmailNotificationTooltip>
                                        <SMSNotificationTooltip>
                                            <Checkbox<JobsAssignmentNotificationTopics>
                                                size="md"
                                                colorScheme="gray"
                                                isDisabled={isLoadingData || phoneNotificationsDisabled}
                                                name="enable_job_notification_job_rescheduled_sms"
                                                isControlled={true}
                                            />
                                        </SMSNotificationTooltip>
                                    </Settings.NotificationConfigPage.Checkboxes>
                                </Property.Value>
                            </Property.Root>
                            <Property.Root>
                                <Property.Content>
                                    <Property.Label>Job unscheduled</Property.Label>
                                    <Property.Subtitle>
                                        Notify me when someone unschedules a job I was assigned.
                                    </Property.Subtitle>
                                </Property.Content>
                                <Property.Value>
                                    <Settings.NotificationConfigPage.Checkboxes>
                                        <EmailNotificationTooltip>
                                            <Checkbox<JobsAssignmentNotificationTopics>
                                                size="md"
                                                colorScheme="gray"
                                                isDisabled={isLoadingData || emailNotificationsDisabled}
                                                name="enable_job_notification_job_unscheduled_email"
                                                isControlled={true}
                                            />
                                        </EmailNotificationTooltip>
                                        <SMSNotificationTooltip>
                                            <Checkbox<JobsAssignmentNotificationTopics>
                                                size="md"
                                                colorScheme="gray"
                                                isDisabled={isLoadingData || phoneNotificationsDisabled}
                                                name="enable_job_notification_job_unscheduled_sms"
                                                isControlled={true}
                                            />
                                        </SMSNotificationTooltip>
                                    </Settings.NotificationConfigPage.Checkboxes>
                                </Property.Value>
                            </Property.Root>
                            <Property.Root>
                                <Property.Content>
                                    <Property.Label>Job cancelled</Property.Label>
                                    <Property.Subtitle>
                                        Notify me when someone cancels a job I was assigned.
                                    </Property.Subtitle>
                                </Property.Content>
                                <Property.Value>
                                    <Settings.NotificationConfigPage.Checkboxes>
                                        <EmailNotificationTooltip>
                                            <Checkbox<JobsAssignmentNotificationTopics>
                                                size="md"
                                                colorScheme="gray"
                                                isDisabled={isLoadingData || emailNotificationsDisabled}
                                                name="enable_job_notification_job_cancelled_email"
                                                isControlled={true}
                                            />
                                        </EmailNotificationTooltip>
                                        <SMSNotificationTooltip>
                                            <Checkbox<JobsAssignmentNotificationTopics>
                                                size="md"
                                                colorScheme="gray"
                                                isDisabled={isLoadingData || phoneNotificationsDisabled}
                                                name="enable_job_notification_job_cancelled_sms"
                                                isControlled={true}
                                            />
                                        </SMSNotificationTooltip>
                                    </Settings.NotificationConfigPage.Checkboxes>
                                </Property.Value>
                            </Property.Root>
                        </PropertyStack>
                    </Settings.NotificationConfigPage.Wrapper>
                )
            }}
        </GenericForm>
    )
}
